/**
 * Onglets conformes au motif WAI-ARIA « tabs » : un seul onglet dans l’ordre de
 * tabulation, flèches pour circuler, `Home`/`End` aux extrémités.
 *
 * Utilisable en composant contrôlé (`actif` + `onChange`) ou non (`defaut`).
 */

import type { KeyboardEvent, ReactNode } from 'react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '../lib/cn'

export interface Onglet {
  readonly id: string
  readonly libelle: ReactNode
  /** Compteur discret à droite du libellé (« 37 »). */
  readonly compteur?: ReactNode | undefined
  readonly contenu?: ReactNode | undefined
  readonly desactive?: boolean | undefined
}

export interface TabsProps {
  readonly onglets: readonly Onglet[]
  /** Mode contrôlé. */
  readonly actif?: string | undefined
  readonly onChange?: ((id: string) => void) | undefined
  /** Mode non contrôlé : onglet ouvert au premier rendu. */
  readonly defaut?: string | undefined
  readonly className?: string | undefined
}

export function Tabs({ onglets, actif, onChange, defaut, className }: TabsProps) {
  const idBase = useId()
  const premier = onglets[0]?.id ?? ''
  const [interne, setInterne] = useState(defaut ?? premier)
  const references = useRef(new Map<string, HTMLButtonElement>())
  const barre = useRef<HTMLDivElement>(null)
  /** Reste-t-il des onglets à gauche / à droite du champ visible ? */
  const [debordement, setDebordement] = useState({ gauche: false, droite: false })

  const courant = actif ?? interne

  /**
   * Sur un téléphone, trois onglets de configuration sur cinq tombent hors
   * champ. La barre défile déjà, mais rien ne le disait : on mesure le
   * débordement pour afficher un dégradé de bord et un repère « › ».
   */
  function mesurerDebordement(): void {
    const element = barre.current
    if (element === null) return
    const marge = 2
    setDebordement((precedent) => {
      const gauche = element.scrollLeft > marge
      const droite = element.scrollLeft + element.clientWidth < element.scrollWidth - marge
      if (gauche === precedent.gauche && droite === precedent.droite) return precedent
      return { gauche, droite }
    })
  }

  useLayoutEffect(() => {
    mesurerDebordement()
    const element = barre.current
    if (element === null) return undefined
    const observateur = new ResizeObserver(mesurerDebordement)
    observateur.observe(element)
    return () => { observateur.disconnect() }
  }, [onglets])

  // L’onglet actif est toujours ramené dans le champ visible : changer d’onglet
  // au clavier ou par programme ne doit jamais laisser la sélection hors écran.
  useEffect(() => {
    references.current.get(courant)?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    mesurerDebordement()
  }, [courant])

  function choisir(id: string): void {
    if (actif === undefined) setInterne(id)
    onChange?.(id)
  }

  function auClavier(evenement: KeyboardEvent<HTMLDivElement>): void {
    const utilisables = onglets.filter((onglet) => onglet.desactive !== true)
    const position = utilisables.findIndex((onglet) => onglet.id === courant)
    if (position < 0) return

    let cible: string | undefined
    if (evenement.key === 'ArrowRight') {
      cible = utilisables[(position + 1) % utilisables.length]?.id
    } else if (evenement.key === 'ArrowLeft') {
      cible = utilisables[(position - 1 + utilisables.length) % utilisables.length]?.id
    } else if (evenement.key === 'Home') {
      cible = utilisables[0]?.id
    } else if (evenement.key === 'End') {
      cible = utilisables[utilisables.length - 1]?.id
    }

    if (cible !== undefined) {
      evenement.preventDefault()
      choisir(cible)
      references.current.get(cible)?.focus()
    }
  }

  const ongletCourant = onglets.find((onglet) => onglet.id === courant)

  return (
    <div className={className}>
      {/* Le dégradé de bord (et non un simple `overflow-x-auto` muet) est le
          seul indice qu’il reste des onglets : sans lui, « Comptes du
          personnel » n’existe pas pour qui travaille sur téléphone. */}
      <div className="relative">
        {debordement.gauche && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-white to-transparent"
          />
        )}
        {debordement.droite && (
          <>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white to-transparent"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-1 z-10 -translate-y-1/2 text-base leading-none text-slate-600"
            >
              ›
            </span>
          </>
        )}
      <div
        ref={barre}
        role="tablist"
        className="flex snap-x snap-proximity gap-1 overflow-x-auto border-b border-slate-200"
        onScroll={mesurerDebordement}
        onKeyDown={auClavier}
      >
        {onglets.map((onglet) => {
          const selectionne = onglet.id === courant
          return (
            <button
              key={onglet.id}
              ref={(element) => {
                if (element === null) references.current.delete(onglet.id)
                else references.current.set(onglet.id, element)
              }}
              type="button"
              role="tab"
              id={`${idBase}-onglet-${onglet.id}`}
              aria-selected={selectionne}
              aria-controls={`${idBase}-panneau-${onglet.id}`}
              tabIndex={selectionne ? 0 : -1}
              disabled={onglet.desactive === true}
              onClick={() => { choisir(onglet.id) }}
              className={cn(
                // 44 px au doigt, 38 px à la souris — un onglet manqué renvoie
                // sur un autre écran, c'est l'erreur la plus coûteuse ici.
                'flex snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors sm:py-2',
                selectionne
                  ? 'border-rideau-700 text-rideau-800'
                  : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
                onglet.desactive === true && 'cursor-not-allowed opacity-50',
              )}
            >
              {onglet.libelle}
              {onglet.compteur !== undefined && (
                <span
                  className={cn(
                    'chiffres-alignes rounded-full px-1.5 py-0.5 text-xs',
                    selectionne ? 'bg-rideau-100 text-rideau-800' : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {onglet.compteur}
                </span>
              )}
            </button>
          )
        })}
      </div>
      </div>

      {ongletCourant?.contenu !== undefined && (
        <div
          role="tabpanel"
          id={`${idBase}-panneau-${ongletCourant.id}`}
          aria-labelledby={`${idBase}-onglet-${ongletCourant.id}`}
          tabIndex={0}
          className="pt-4"
        >
          {ongletCourant.contenu}
        </div>
      )}
    </div>
  )
}
